import { Box } from "@mui/material"
import type { ColumnSort, ExpandedState, OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import type { FC, Ref } from "react"
import React, { useState } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"
import type { Instruction } from "@/graphql/codegen/graphql"
import ExpandedInstructionRow from "@/screens/ManageProducts/components/Instructions/components/instructionProducts/ExpandedInstructionRow"
import { useInstructionsColumns } from "@/screens/ManageProducts/components/Instructions/hooks/useInstructionsColumns"
import type { InstructionDialogs } from "@/screens/ManageProducts/components/Instructions/types"

interface InstructionTableProps {
  data: Instruction[]
  fetching: boolean
  scrollHandler: () => void
  setSelectedInstruction: (instruction: Instruction | null) => void
  setDialogOpen: (dialog: InstructionDialogs | null) => void
  tableContainerRef?: Ref<HTMLDivElement>
  handleSortingChange: (updaterFn: (state: SortingState) => SortingState) => void
  sorting: ColumnSort[]
  error?: Error
}
const InstructionTable: FC<InstructionTableProps> = ({
  data,
  error,
  scrollHandler,
  handleSortingChange,
  setSelectedInstruction,
  setDialogOpen,
  sorting,
  tableContainerRef,
  fetching,
}) => {
  const [expandedRow, setExpandedRow] = useState<ExpandedState>({})

  const [shouldRefetch, setShouldRefetch] = useState<Record<number, boolean>>({})
  const columns = useInstructionsColumns({
    setSelectedInstruction,
    setDialogOpen,
    refetchProductsList: (id: number) => {
      setShouldRefetch((prev) => {
        return { ...prev, [id]: true }
      })
    },
  })
  return (
    <Box className='flex h-full flex-col'>
      <AerosBaseTable
        error={error}
        features={{
          table: {
            stickyHeader: true,
          },
          tanstackOptions: {
            data,
            columns,
            state: {
              sorting,
              isLoading: fetching,
              expanded: expandedRow,
            },
            manualSorting: true,
            enableSorting: true,
            enableMultiSort: false,
            enableSortingRemoval: false,
            getCoreRowModel: getCoreRowModel(),
            onSortingChange: handleSortingChange as OnChangeFn<SortingState>,
            onExpandedChange: setExpandedRow,
            getRowCanExpand: () => true,
            renderExpandedContent: (row: Row<Instruction>) => (
              <ExpandedInstructionRow
                row={row}
                shouldRefetch={shouldRefetch[Number(row.id)]}
                setShouldRefetch={setShouldRefetch}
              />
            ),
          },
        }}
        slotProps={{
          container: {
            ref: tableContainerRef,
            onScroll: scrollHandler,
          },
          bodyCellExpanded: { className: "bg-primary-100" },
          expandedCell: expandedCellStyles,
          bodyRow: {
            className: "hover:bg-primary-100 cursor-pointer",
          },
        }}
        noDataMessage='No instructions found'
      />
    </Box>
  )
}

export default InstructionTable
